import React from 'react';
import {
    List,
    Datagrid,
    TextField,
    DateField,
    EditButton,
    TextInput,
    TopToolbar,
    ExportButton,
    RefreshButton,
    CreateButton,
    useTranslate,
    FunctionField,
    usePermissions,
    useListContext,
    RecordContextProvider,
} from 'react-admin';
import { Box, Chip, Typography, Fab, useMediaQuery, useTheme } from '@mui/material';
import {
    Security as SecurityIcon,
    People as PeopleIcon,
    Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const RoleFilters = [
    <TextInput
        key="search"
        source="q"
        label="resources.roles.list.filters.search"
        alwaysOn
        sx={{
            '& .MuiInputBase-root': {
                backgroundColor: 'white'
            }
        }}
    />,
];

const DesktopListActions = () => {
    const { permissions } = usePermissions();
    const canCreate = permissions?.includes('roles:write:all');

    return (
        <TopToolbar>
            {canCreate && <CreateButton />}
            <ExportButton />
            <RefreshButton />
        </TopToolbar>
    );
};

const MobileListActions = () => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, p: 0 }}>
        <ExportButton />
        <RefreshButton />
    </Box>
);

const MobileList = () => {
    const { data, isLoading } = useListContext();
    const navigate = useNavigate();
    const translate = useTranslate();
    if (isLoading || !data) return null;
    return (
        <Box>
            {data.map((record: any) => (
                <RecordContextProvider key={record.id} value={record}>
                    <Box
                        onClick={() => navigate(`/roles/${record.id}`)}
                        sx={{ py: 1.5, px: 2, borderBottom: '1px solid #F0F0F0', cursor: 'pointer' }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                            <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                                {record.name}
                            </Typography>
                            <Chip
                                icon={<SecurityIcon />}
                                label={`${record.permissionCount || 0} ${translate('resources.roles.permissions', { smart_count: record.permissionCount || 0 })}`}
                                size="small"
                                sx={{ flexShrink: 0, backgroundColor: '#009A7515', color: '#009A75', fontWeight: 500 }}
                            />
                        </Box>
                        {record.description && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mb: 0.5 }}>
                                {record.description}
                            </Typography>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                                icon={<PeopleIcon />}
                                label={`${record.userCount || 0} ${translate('resources.roles.list.users', { smart_count: record.userCount || 0 })}`}
                                size="small"
                                variant="outlined"
                                sx={{ borderColor: '#E0E0E0', color: '#666666' }}
                            />
                            {record.createdAt && (
                                <Typography variant="caption" color="text.secondary">
                                    {new Date(record.createdAt).toLocaleDateString()}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </RecordContextProvider>
            ))}
        </Box>
    );
};

const RolesList = () => {
    const translate = useTranslate();
    const { permissions } = usePermissions();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();

    if (!permissions?.includes('roles:read:all')) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography>{translate('ra.page.not_found')}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ position: 'relative' }}>
            <List
                filters={RoleFilters}
                actions={isMobile ? <MobileListActions /> : <DesktopListActions />}
                empty={isMobile ? false : undefined}
                perPage={25}
                sort={{ field: 'createdAt', order: 'DESC' }}
                sx={{
                    '& .RaList-content': {
                        backgroundColor: isMobile ? 'transparent' : '#FAFAFA',
                    },
                    ...(isMobile && {
                        '& .RaList-main': { margin: 0 },
                        '& .RaList-content': { backgroundColor: 'transparent !important', boxShadow: 'none !important', mt: 2 },
                        '& .RaList-actions': { backgroundColor: 'transparent !important', p: '0 !important', minHeight: 'auto !important' },
                        '& .RaListToolbar-root': { backgroundColor: 'transparent !important', flexDirection: 'row !important', flexWrap: 'wrap !important', alignItems: 'center !important', gap: '8px !important' },
                        '& .RaListToolbar-root > span': { display: 'none' },
                        '& .RaFilterButton-root, & .add-filter': { display: 'none !important' },
                        '& .RaListToolbar-root > form, & .RaFilterForm-root': { flex: '1 1 100% !important', width: '100% !important' },
                        '& .filter-field, & .RaFilterForm-filterFormInput': { width: '100% !important' },
                        '& .filter-field .MuiFormControl-root': { width: '100% !important' },
                        '& .filter-field .MuiInputBase-root': { width: '100% !important' },
                        '& .MuiToolbar-root': { backgroundColor: 'transparent !important', minHeight: 'auto !important' },
                    }),
                }}
            >
                {isMobile ? (
                    <MobileList />
                ) : (
                    <Datagrid
                        bulkActionButtons={false}
                        sx={{
                            '& .RaDatagrid-headerCell': {
                                backgroundColor: '#F5F5F5',
                                fontWeight: 'bold',
                            },
                            '& .RaDatagrid-row': {
                                '&:hover': {
                                    backgroundColor: '#F0F7FF',
                                },
                            },
                        }}
                    >
                        <TextField source="name" label={translate('resources.roles.fields.name')} />

                        <TextField
                            source="description"
                            label={translate('resources.roles.fields.description')}
                            sx={{
                                maxWidth: 300,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        />

                        <FunctionField
                            label={translate('resources.roles.fields.permissions')}
                            render={(record: any) => {
                                const permissionCount = record.permissionCount || 0;
                                return (
                                    <Chip
                                        icon={<SecurityIcon />}
                                        label={`${permissionCount} ${translate('resources.roles.permissions', { smart_count: permissionCount })}`}
                                        size="small"
                                        sx={{
                                            backgroundColor: '#009A7515',
                                            color: '#009A75',
                                            fontWeight: 500,
                                        }}
                                    />
                                );
                            }}
                        />

                        <FunctionField
                            label={translate('resources.roles.fields.users')}
                            render={(record: any) => {
                                const userCount = record.userCount || 0;
                                return (
                                    <Chip
                                        icon={<PeopleIcon />}
                                        label={`${userCount} ${translate('resources.roles.list.users', { smart_count: userCount })}`}
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                            borderColor: '#E0E0E0',
                                            color: '#666666',
                                        }}
                                    />
                                );
                            }}
                        />

                        <DateField
                            source="createdAt"
                            label={translate('resources.roles.fields.createdAt')}
                            locales="en-US"
                            options={{
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                            }}
                            sortable={true}
                        />

                        <EditButton />
                    </Datagrid>
                )}
            </List>
            {isMobile && permissions?.includes('roles:write:all') && (
                <Fab
                    color="primary"
                    onClick={() => navigate('/roles/create')}
                    sx={{
                        position: 'fixed',
                        bottom: 24,
                        right: 24,
                        backgroundColor: '#009A75',
                        '&:hover': { backgroundColor: '#007A5E' },
                        zIndex: 1100,
                    }}
                >
                    <AddIcon />
                </Fab>
            )}
        </Box>
    );
};

export default RolesList;
